import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSimulation } from "@/context/SimulationContext";
import { postSimulate } from "@/lib/api";
import { fmtRp, fmtNum } from "@/lib/format";
import { exportSimulationPdf } from "@/lib/pdfExport";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Download, MessageCircle, Settings2, Sprout, ArrowRight, ArrowLeft, Info } from "lucide-react";

const STEPS = [
  { key: "A", title: "Data Rumah" },
  { key: "B", title: "Pola Pemakaian" },
  { key: "C", title: "Prioritas & Backup" },
  { key: "D", title: "Atap" },
  { key: "E", title: "Hasil" },
];

const VA_OPTIONS = [1300, 2200, 3500, 4400, 5500, 7700, 11000];
const PERALATAN = ["AC", "Pompa air", "Freezer", "Water heater", "EV charger", "Oven listrik"];

export default function Simulasi() {
  const nav = useNavigate();
  const { input, updateInput, result, setResult, setSelectedTier } = useSimulation();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const progress = useMemo(() => ((step + 1) / STEPS.length) * 100, [step]);

  const canNext = () => {
    if (step === 0) return !!(input.daya_pln_va || input.daya_pln_lain);
    if (step === 1) return (input.siang_pct + input.malam_pct) === 100;
    return true;
  };

  const runSim = async () => {
    setLoading(true);
    try {
      const r = await postSimulate(input);
      setResult(r);
      setStep(4);
      toast.success("Simulasi selesai");
    } catch (e) {
      toast.error("Gagal menjalankan simulasi: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const goTo = (i) => { if (i < 4) setStep(i); };
  const next = async () => {
    if (!canNext()) { toast.error("Lengkapi data pada langkah ini"); return; }
    if (step === 3) { await runSim(); return; }
    setStep((s) => Math.min(4, s + 1));
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div className="pe-mobile-cta-safe" data-testid="page-simulasi">
      <div className="pe-container pt-10 pb-6">
        <div className="pe-eyebrow">Simulasi</div>
        <h1 className="mt-2 font-display font-bold text-3xl lg:text-4xl">Lima langkah untuk hasil yang bisa dibandingkan</h1>
        <p className="mt-3 text-[color:var(--pe-text-muted)] max-w-2xl">Simulasi awal tanpa akun. Bapak/Ibu bisa memilih "Belum tahu" bila data tertentu memerlukan survei.</p>
      </div>

      <div className="pe-container">
        <div className="flex flex-wrap gap-2 items-center mb-3">
          {STEPS.map((s, i) => (
            <button
              key={s.key}
              onClick={() => goTo(i)}
              disabled={i > step || i === 4}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                i === step
                  ? "bg-[color:var(--pe-primary)] text-white border-[color:var(--pe-primary)]"
                  : i < step
                  ? "border-[color:var(--pe-primary)] text-[color:var(--pe-primary)]"
                  : "border-slate-200 text-slate-400"
              }`}
              data-testid={`wizard-step-chip-${s.key}`}
            >
              {s.key}. {s.title}
            </button>
          ))}
        </div>
        <Progress value={progress} data-testid="wizard-progress" />
      </div>

      <div className="pe-container mt-8 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          {step === 0 && <StepA input={input} updateInput={updateInput} />}
          {step === 1 && <StepB input={input} updateInput={updateInput} />}
          {step === 2 && <StepC input={input} updateInput={updateInput} />}
          {step === 3 && <StepD input={input} updateInput={updateInput} />}
          {step === 4 && result && <StepE result={result} input={input} onPick={(t) => { setSelectedTier(t); nav("/konfigurator"); }} />}

          {step < 4 && (
            <div className="mt-8 flex justify-between items-center">
              <Button variant="ghost" onClick={prev} disabled={step === 0} data-testid="wizard-prev-btn">
                <ArrowLeft size={16} className="mr-1" /> Kembali
              </Button>
              <Button className="pe-btn-primary" onClick={next} disabled={loading} data-testid="wizard-next-btn">
                {loading ? "Menghitung..." : step === 3 ? "Lihat Hasil" : "Lanjut"} <ArrowRight size={16} className="ml-1" />
              </Button>
            </div>
          )}
        </div>

        <aside className="lg:col-span-4">
          <div className="pe-card sticky top-24">
            <div className="pe-eyebrow">Ringkasan input</div>
            <ul className="mt-3 text-sm space-y-1.5 text-[color:var(--pe-text-muted)]">
              <li>Kota: <span className="text-[color:var(--pe-text)]">{input.kota || "—"}</span></li>
              <li>Daya PLN: <span className="text-[color:var(--pe-text)]">{input.daya_pln_lain || input.daya_pln_va || "—"} VA · {input.fase}</span></li>
              <li>Konsumsi: <span className="text-[color:var(--pe-text)]">{input.kwh_bulanan ? `${input.kwh_bulanan} kWh/bulan` : input.tagihan_rp ? fmtRp(input.tagihan_rp) + "/bulan" : "—"}</span></li>
              <li>Siang/Malam: <span className="text-[color:var(--pe-text)]">{input.siang_pct}% / {input.malam_pct}%</span></li>
              <li>Prioritas: <span className="text-[color:var(--pe-text)]">{input.prioritas}</span></li>
              <li>Backup: <span className="text-[color:var(--pe-text)]">{input.beban_backup_kw} kW · {input.durasi_backup_jam} jam</span></li>
              <li>Atap: <span className="text-[color:var(--pe-text)]">{input.luas_atap_m2 ? `${input.luas_atap_m2} m²` : "—"}</span></li>
            </ul>
            <div className="mt-5 p-3 rounded-lg text-xs" style={{ background: "var(--pe-surface)", color: "var(--pe-primary)" }}>
              <Info size={14} className="inline mr-1" /> Semua angka hasil adalah estimasi dengan asumsi yang bisa diperiksa. Bukan janji.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, help, children, testid }) {
  return (
    <div className="mb-5" data-testid={testid}>
      <Label className="font-medium">{label}</Label>
      {help && <p className="text-xs text-[color:var(--pe-text-muted)] mt-0.5 mb-2">{help}</p>}
      <div className="mt-2">{children}</div>
    </div>
  );
}

function StepA({ input, updateInput }) {
  return (
    <div className="pe-card" data-testid="wizard-step-A">
      <h2 className="font-display font-semibold text-xl">A. Data rumah</h2>
      <p className="text-sm text-[color:var(--pe-text-muted)] mt-1">Dasar untuk menghitung konsumsi dan ruang desain sistem.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <Field label="Kota / Kabupaten" testid="field-kota">
          <Input value={input.kota || ""} onChange={(e) => updateInput({ kota: e.target.value })} placeholder="Mis. Jakarta Selatan" data-testid="input-kota" />
        </Field>
        <Field label="Tipe bangunan" testid="field-tipe">
          <Select value={input.tipe_bangunan} onValueChange={(v) => updateInput({ tipe_bangunan: v })}>
            <SelectTrigger data-testid="select-tipe"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="rumah">Rumah tinggal</SelectItem>
              <SelectItem value="ruko">Ruko</SelectItem>
              <SelectItem value="kantor-kecil">Kantor kecil</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Daya PLN terpasang" help="VA bukan konsumsi kWh. Pilih yang paling dekat." testid="field-daya">
          <Select value={String(input.daya_pln_va)} onValueChange={(v) => updateInput({ daya_pln_va: Number(v), daya_pln_lain: null })}>
            <SelectTrigger data-testid="select-daya"><SelectValue /></SelectTrigger>
            <SelectContent>
              {VA_OPTIONS.map((v) => <SelectItem key={v} value={String(v)}>{v} VA</SelectItem>)}
              <SelectItem value="0">Lain / belum tahu</SelectItem>
            </SelectContent>
          </Select>
          {input.daya_pln_va === 0 && (
            <Input className="mt-2" type="number" placeholder="VA, mis. 13200" value={input.daya_pln_lain || ""} onChange={(e) => updateInput({ daya_pln_lain: Number(e.target.value) })} data-testid="input-daya-lain" />
          )}
        </Field>
        <Field label="Fase" testid="field-fase">
          <Select value={input.fase} onValueChange={(v) => updateInput({ fase: v })}>
            <SelectTrigger data-testid="select-fase"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1-phase">Satu fase</SelectItem>
              <SelectItem value="3-phase">Tiga fase</SelectItem>
              <SelectItem value="unknown">Belum tahu</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Konsumsi kWh/bulan" help="Jika tahu, isi angka ini. Lebih akurat dari estimasi dari rupiah." testid="field-kwh">
          <Input type="number" value={input.kwh_bulanan || ""} onChange={(e) => updateInput({ kwh_bulanan: e.target.value ? Number(e.target.value) : null })} placeholder="Mis. 450" data-testid="input-kwh" />
        </Field>
        <Field label="Tagihan Rp/bulan (opsional)" help="Dipakai hanya bila kWh tidak tersedia. Hilangkan pajak/admin bila bisa." testid="field-tagihan">
          <Input type="number" value={input.tagihan_rp || ""} onChange={(e) => updateInput({ tagihan_rp: e.target.value ? Number(e.target.value) : null })} placeholder="Mis. 650000" data-testid="input-tagihan" />
        </Field>
        <Field label="Tarif per kWh (Rp)" help="Default 1.445 (contoh). Admin dapat memperbarui per golongan." testid="field-tarif">
          <Input type="number" value={input.tarif_per_kwh} onChange={(e) => updateInput({ tarif_per_kwh: Number(e.target.value) })} data-testid="input-tarif" />
        </Field>
        <Field label="Hari dalam periode" testid="field-hari">
          <Input type="number" value={input.hari_periode} onChange={(e) => updateInput({ hari_periode: Number(e.target.value) })} data-testid="input-hari" />
        </Field>
      </div>
    </div>
  );
}

function StepB({ input, updateInput }) {
  const sum = input.siang_pct + input.malam_pct;
  return (
    <div className="pe-card" data-testid="wizard-step-B">
      <h2 className="font-display font-semibold text-xl">B. Pola pemakaian</h2>
      <p className="text-sm text-[color:var(--pe-text-muted)] mt-1">Persentase konsumsi siang dan malam. Totalnya harus 100%.</p>
      <div className="mt-6">
        <div className="flex gap-3 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => updateInput({ siang_pct: 65, malam_pct: 35 })} data-testid="preset-dominan-siang">Dominan siang 65/35</Button>
          <Button variant="outline" size="sm" onClick={() => updateInput({ siang_pct: 50, malam_pct: 50 })} data-testid="preset-seimbang">Seimbang 50/50</Button>
          <Button variant="outline" size="sm" onClick={() => updateInput({ siang_pct: 35, malam_pct: 65 })} data-testid="preset-dominan-malam">Dominan malam 35/65</Button>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <Field label="Siang (%)" testid="field-siang">
            <Input type="number" value={input.siang_pct} onChange={(e) => updateInput({ siang_pct: Number(e.target.value), malam_pct: 100 - Number(e.target.value) })} data-testid="input-siang" />
          </Field>
          <Field label="Malam (%)" testid="field-malam">
            <Input type="number" value={input.malam_pct} onChange={(e) => updateInput({ malam_pct: Number(e.target.value), siang_pct: 100 - Number(e.target.value) })} data-testid="input-malam" />
          </Field>
        </div>
        <div className={`text-xs mt-2 ${sum === 100 ? "text-[color:var(--pe-success)]" : "text-[color:var(--pe-danger)]"}`} data-testid="day-night-sum-warning">
          Total: {sum}% {sum !== 100 && "— perlu 100%"}
        </div>
      </div>

      <div className="mt-6">
        <Label className="font-medium">Peralatan besar di rumah (opsional)</Label>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {PERALATAN.map((p) => (
            <label key={p} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={input.peralatan_besar.includes(p)}
                onCheckedChange={(ck) => {
                  const arr = ck ? [...input.peralatan_besar, p] : input.peralatan_besar.filter((x) => x !== p);
                  updateInput({ peralatan_besar: arr });
                }}
                data-testid={`checkbox-peralatan-${p.replace(/\s/g, "-").toLowerCase()}`}
              />
              {p}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function StepC({ input, updateInput }) {
  return (
    <div className="pe-card" data-testid="wizard-step-C">
      <h2 className="font-display font-semibold text-xl">C. Prioritas & backup</h2>
      <p className="text-sm text-[color:var(--pe-text-muted)] mt-1">Apa yang paling penting dari sistem PLTS untuk rumah Bapak/Ibu.</p>
      <div className="mt-6">
        <Label className="font-medium">Prioritas utama</Label>
        <RadioGroup value={input.prioritas} onValueChange={(v) => updateInput({ prioritas: v })} className="mt-3 grid sm:grid-cols-3 gap-3">
          {[
            { v: "hemat", t: "Hemat pembelian listrik" },
            { v: "backup", t: "Jaga beban penting saat padam" },
            { v: "seimbang", t: "Seimbang keduanya" },
          ].map((p) => (
            <label key={p.v} className={`cursor-pointer rounded-lg border p-4 ${input.prioritas === p.v ? "border-[color:var(--pe-primary)] bg-[color:var(--pe-surface)]" : "border-slate-200"}`}>
              <RadioGroupItem value={p.v} id={`prio-${p.v}`} className="sr-only" data-testid={`radio-prioritas-${p.v}`} />
              <div className="text-sm font-medium">{p.t}</div>
            </label>
          ))}
        </RadioGroup>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <Field label="Beban penting saat backup (kW)" help="Estimasi kebutuhan daya beban prioritas." testid="field-beban-backup">
          <Input type="number" step="0.1" value={input.beban_backup_kw} onChange={(e) => updateInput({ beban_backup_kw: Number(e.target.value) })} data-testid="input-beban-backup" />
        </Field>
        <Field label="Durasi backup yang diinginkan (jam)" testid="field-durasi-backup">
          <Input type="number" step="0.5" value={input.durasi_backup_jam} onChange={(e) => updateInput({ durasi_backup_jam: Number(e.target.value) })} data-testid="input-durasi" />
        </Field>
        <Field label="Reserve SOC minimum (%)" help="Baterai menyimpan cadangan ini untuk pemadaman." testid="field-reserve">
          <Input type="number" value={input.reserve_pct} onChange={(e) => updateInput({ reserve_pct: Number(e.target.value) })} data-testid="input-reserve" />
        </Field>
        <Field label="Anggaran (Rp, opsional)" testid="field-anggaran">
          <Input type="number" value={input.anggaran_rp || ""} onChange={(e) => updateInput({ anggaran_rp: e.target.value ? Number(e.target.value) : null })} placeholder="Mis. 150000000" data-testid="input-anggaran" />
        </Field>
      </div>
    </div>
  );
}

function StepD({ input, updateInput }) {
  return (
    <div className="pe-card" data-testid="wizard-step-D">
      <h2 className="font-display font-semibold text-xl">D. Atap</h2>
      <p className="text-sm text-[color:var(--pe-text-muted)] mt-1">Boleh "belum tahu" — kelayakan struktur ditentukan saat survei.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <Field label="Luas atap yang bisa dipakai (m²)" testid="field-luas-atap">
          <Input type="number" value={input.luas_atap_m2 || ""} onChange={(e) => updateInput({ luas_atap_m2: e.target.value ? Number(e.target.value) : null })} placeholder="Mis. 30" data-testid="input-luas-atap" />
        </Field>
        <Field label="Jenis atap" testid="field-jenis-atap">
          <Select value={input.jenis_atap} onValueChange={(v) => updateInput({ jenis_atap: v })}>
            <SelectTrigger data-testid="select-jenis-atap"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="beton">Beton</SelectItem>
              <SelectItem value="genteng-tanah">Genteng tanah</SelectItem>
              <SelectItem value="genteng-beton">Genteng beton</SelectItem>
              <SelectItem value="metal">Metal</SelectItem>
              <SelectItem value="unknown">Belum tahu</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Arah bidang atap utama" testid="field-arah">
          <Select value={input.arah_atap} onValueChange={(v) => updateInput({ arah_atap: v })}>
            <SelectTrigger data-testid="select-arah"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="utara">Utara</SelectItem>
              <SelectItem value="selatan">Selatan</SelectItem>
              <SelectItem value="timur">Timur</SelectItem>
              <SelectItem value="barat">Barat</SelectItem>
              <SelectItem value="unknown">Belum tahu</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Bayangan" testid="field-bayangan">
          <Select value={input.bayangan} onValueChange={(v) => updateInput({ bayangan: v })}>
            <SelectTrigger data-testid="select-bayangan"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="minimal">Minimal</SelectItem>
              <SelectItem value="sedang">Sedang</SelectItem>
              <SelectItem value="banyak">Banyak</SelectItem>
              <SelectItem value="unknown">Belum tahu</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
    </div>
  );
}

function StepE({ result, input, onPick }) {
  const tiers = ["Starter", "Safer", "Signature"];
  return (
    <div data-testid="wizard-step-E">
      {/* A — Data Client */}
      <div className="pe-card">
        <div className="pe-eyebrow">A — Data Client</div>
        <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Row k="Kota" v={input.kota || "—"} />
          <Row k="Daya PLN" v={`${input.daya_pln_lain || input.daya_pln_va} VA · ${input.fase}`} />
          <Row k="Konsumsi" v={input.kwh_bulanan ? `${input.kwh_bulanan} kWh/bulan` : input.tagihan_rp ? `${fmtRp(input.tagihan_rp)}/bulan` : "—"} />
          <Row k="Prioritas" v={input.prioritas} />
          <Row k="Siang / Malam" v={`${input.siang_pct}% / ${input.malam_pct}%`} />
          <Row k="Anggaran" v={input.anggaran_rp ? fmtRp(input.anggaran_rp) : "—"} />
        </div>
      </div>

      {/* B — Perhitungan Konsumsi */}
      <div className="pe-card mt-5">
        <div className="pe-eyebrow">B — Perhitungan Konsumsi</div>
        <div className="mt-3 grid sm:grid-cols-3 gap-4 text-sm">
          <Stat k="Konsumsi harian" v={`${fmtNum(result.options?.Safer?.daily_kwh_consumption || result.options?.Starter?.daily_kwh_consumption, 2)} kWh`} />
          <Stat k="PSH dipakai" v={result.psh_used} />
          <Stat k="Tarif referensi" v={`${fmtRp(result.tarif_used)}/kWh`} />
        </div>
      </div>

      {/* C — Kebutuhan Sistem */}
      <div className="pe-eyebrow mt-8">C — Perbandingan 3 Opsi</div>
      <div className="mt-3 grid lg:grid-cols-3 gap-5">
        {tiers.map((t) => {
          const o = result.options[t];
          if (!o) return null;
          const best = result.paling_sesuai_tier === t;
          const variant = t === "Starter" ? "pkg-starter" : t === "Safer" ? "pkg-safer" : "pkg-signature";
          const isDark = t === "Signature";
          return (
            <div key={t} className={`pe-card ${variant} relative`} data-testid={`result-card-${t.toLowerCase()}`}>
              {best && (
                <div className="absolute -top-3 left-5 text-xs font-bold px-3 py-1 rounded-full" style={{ background: "var(--pe-primary)", color: "white" }}>
                  Paling sesuai
                </div>
              )}
              <div className={`pe-eyebrow ${isDark ? "!text-cyan-300" : ""}`}>{t}</div>
              <div className={`mt-1 font-display font-bold text-2xl ${isDark ? "text-white" : ""}`}>{o.package.kwp} kWp</div>
              <ul className={`mt-3 space-y-1.5 text-sm ${isDark ? "text-slate-200" : "text-[color:var(--pe-text-muted)]"}`}>
                <li>{o.package.panels} panel × {o.package.panel_wp} Wp</li>
                <li>Inverter {o.package.inverter_kw} kW</li>
                <li>Baterai {o.package.battery_units} × {o.package.battery_kwh_per_unit} kWh ({fmtNum(o.package.battery_nominal_kwh, 2)} kWh nominal, {fmtNum(o.package.battery_usable_kwh, 2)} usable)</li>
              </ul>
              <div className={`mt-4 pt-4 border-t grid grid-cols-2 gap-y-2 text-sm ${isDark ? "border-white/15 text-slate-200" : "border-slate-200"}`}>
                <div>
                  <div className="text-xs opacity-70">Produksi PV</div>
                  <div className="font-mono-spec">{fmtNum(o.pv_daily_kwh, 2)} kWh/hari</div>
                </div>
                <div>
                  <div className="text-xs opacity-70">Energi offset</div>
                  <div className="font-mono-spec">{fmtNum(o.energy_offset_daily_kwh, 2)} kWh · {o.coverage_pct}%</div>
                </div>
                <div>
                  <div className="text-xs opacity-70">Hemat ~</div>
                  <div className="font-mono-spec">{fmtRp(o.savings_rp_month)}/bulan</div>
                </div>
                <div>
                  <div className="text-xs opacity-70">Backup est.</div>
                  <div className="font-mono-spec">{o.backup_hours_est} jam</div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs opacity-70">Harga referensi</div>
                  <div className={`font-mono-spec font-semibold ${isDark ? "text-cyan-200" : ""}`}>{fmtRp(o.price_rp)}</div>
                </div>
              </div>
              {o.issues?.length > 0 && (
                <ul className={`mt-3 space-y-1 text-xs ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                  {o.issues.map((it, i) => <li key={i}>• {it}</li>)}
                </ul>
              )}
              <div className="mt-5 flex flex-col gap-2">
                <Button className="pe-btn-primary w-full justify-center" onClick={() => onPick(t)} data-testid={`result-select-${t.toLowerCase()}`}>
                  Pilih & sesuaikan <Settings2 size={16} />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* D — Summary */}
      <div className="pe-card mt-8">
        <div className="pe-eyebrow">D — Summary</div>
        <div className="mt-3 text-sm">
          <p><strong>Paling sesuai:</strong> {result.paling_sesuai_tier || "Perlu desain khusus"}</p>
          {result.reasoning && <p className="mt-1 text-[color:var(--pe-text-muted)]">Alasan: {result.reasoning}</p>}
          {result.note_if_none && <p className="mt-2 text-amber-700">{result.note_if_none}</p>}
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <Button className="pe-btn-primary" onClick={() => exportSimulationPdf(input, result)} data-testid="export-pdf-button">
            <Download size={16} /> Unduh ringkasan PDF
          </Button>
          <a
            href={`https://wa.me/6285121341168?text=${encodeURIComponent(`Halo Proper Energy, saya sudah menjalankan simulasi.\nDaya PLN ${input.daya_pln_lain || input.daya_pln_va} VA, prioritas ${input.prioritas}, paling sesuai ${result.paling_sesuai_tier || '-'}. Mohon dibantu konsultasi lanjut.`)}`}
            target="_blank" rel="noopener noreferrer"
            className="pe-btn-ghost"
            data-testid="whatsapp-consultation-button"
          >
            <MessageCircle size={16} /> Konsultasi via WhatsApp
          </a>
          <Button variant="outline" onClick={() => window.location.href = "/rencana-tumbuh"} data-testid="btn-rencana-tumbuh">
            <Sprout size={16} className="mr-1" /> Lihat Rencana Tumbuh
          </Button>
        </div>
      </div>

      {/* Assumptions */}
      <details className="pe-card mt-5" data-testid="assumptions-panel">
        <summary className="cursor-pointer font-semibold text-sm">Asumsi perhitungan ({result.assumptions_version})</summary>
        <div className="mt-3 text-xs text-[color:var(--pe-text-muted)] grid sm:grid-cols-2 gap-x-6 gap-y-1 font-mono-spec">
          <div>Panel: {result.assumptions.panel_wp} Wp</div>
          <div>Baterai/modul: {result.assumptions.battery_kwh_per_unit} kWh</div>
          <div>Loss agregat: {(result.assumptions.pv_loss_factor * 100).toFixed(0)}%</div>
          <div>DoD: {(result.assumptions.dod * 100).toFixed(0)}%</div>
          <div>SOC min default: {(result.assumptions.soc_min_default * 100).toFixed(0)}%</div>
          <div>Eff charge/discharge: {(result.assumptions.eff_charge * 100).toFixed(0)}% / {(result.assumptions.eff_discharge * 100).toFixed(0)}%</div>
          <div>PSH normal: {result.assumptions.psh_normal}</div>
          <div>PSH konservatif: {result.assumptions.psh_conservative}</div>
        </div>
        <p className="mt-3 text-xs text-[color:var(--pe-text-muted)]">{result.assumptions.notes}</p>
      </details>
    </div>
  );
}

const Row = ({ k, v }) => (<><div className="text-[color:var(--pe-text-muted)]">{k}</div><div className="font-medium">{v}</div></>);
const Stat = ({ k, v }) => (<div><div className="text-xs text-[color:var(--pe-text-muted)]">{k}</div><div className="font-mono-spec text-lg">{v}</div></div>);
